import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Dimmer, Grid, Loader } from "semantic-ui-react";
import {
  Process,
  ProcessFormValues,
  useDeleteRecipeProcess,
  useGetRecipeById,
  useUpdateRecipeProcess
} from "../../../api/recipeSimulator";
import { useGetCountries } from "../../../api/static-data";
import { CommonModal, DropDown, InputText, InputTextArea } from "../../../components";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import { InputNumber } from "../../../components/InputText/InputNumber";
import InputWithDropDown from "../../../components/inputWithDropDown/InputWithDropDown";
import MainBottomMultipleButtonView from "../../../components/mainBottomButtonView/MainBottomMultipleButtonView";
import { dataTypeOptions, DEFAULT_COUNTRY_CODE } from "../../../config/constants";
import { IngredientDataTypesOption } from "../../../config/enums/IngredientType.enum";
import { MEASUREMENT_TYPES } from "../../../config/enums/MeasurementTypes";
import { RecipeType } from "../../../config/enums/recipeSimulator.enum";
import { WEIGHT_UNIT_TYPES_OPTIONs } from "../../../config/optionsConstants";
import { errorView } from "../../../helpers/ErrorHandler";
import {
  getExistingProcessesInContext,
  isProcessNameUnique
} from "../../../helpers/ProcessValidationHelper";
import {
  buildRecipeProcessPayload,
  normalizeProcessForForm,
  toNullableApiNumber
} from "../../../helpers/ProcessPayloadHelper";
import { valueGraterThanZero } from "../../../utils/utils";
import "./RecipeAddProcessingStepModal.scss";

interface RecipeAddProcessingStepModalProps {
  visibleModal: boolean;
  setVisibleModal: (visible: boolean) => void;
  process: Process;
  setSelectedProcess: (process: Process | null) => void;
  isOriginal: boolean;
  recipeDataRefetch: () => void;
  variantId?: string | null;
  setVariantId?: (id: string | null) => void;
  genericProcess?: any;
}

const RecipeEditProcessingStepModal = ({
  visibleModal,
  setVisibleModal,
  process,
  setSelectedProcess,
  isOriginal,
  recipeDataRefetch,
  variantId,
  setVariantId,
  genericProcess
}: RecipeAddProcessingStepModalProps) => {
  const { recipeId } = useParams();
  const { data: recipeData } = useGetRecipeById(recipeId);
  const [country, setCountry] = useState<number>();
  const [visibleDeleteModal, setVisibleDeleteModal] = useState<boolean>(false);
  const [dataType, setDataType] = useState<string | undefined>(IngredientDataTypesOption.SPECIFIC);
  const [processType, setProcessType] = useState<string | null>(null);

  const [unitTypes, setUnitTypes] = useState({
    inputUnit: MEASUREMENT_TYPES.WEIGHT_KG,
    outputUnit: MEASUREMENT_TYPES.WEIGHT_KG,
    sideStreamUnit: MEASUREMENT_TYPES.WEIGHT_KG
  });

  const modalClose = () => {
    setVisibleModal(false);
    setSelectedProcess(null);
    recipeDataRefetch();
    setVariantId?.(null);
  };

  const {
    data: countriesData,
    isSuccess: isCountriesDataSuccess,
    isLoading: isCountriesDataLoading
  } = useGetCountries();

  const dataTypeIsGeneric = useMemo(() => {
    return dataType === IngredientDataTypesOption.GENERIC;
  }, [dataType]);

  const countries = useMemo(() => {
    if (isCountriesDataSuccess) {
      const getCountryData = countriesData?.map((c: any) => ({
        key: c.id,
        text: c.country,
        value: c.id
      }));
      return getCountryData;
    }
  }, [countriesData, isCountriesDataSuccess]);

  const getDefaultCountry = useCallback(() => {
    const denmarkValue = countries?.find((e: any) => e.code === DEFAULT_COUNTRY_CODE);
    return denmarkValue?.key;
  }, [countries]);

  const values = useMemo((): ProcessFormValues => {
    setDataType(process?.dataType || IngredientDataTypesOption.SPECIFIC);
    setProcessType(process?.genericProcessId?.toString() || null);
    setCountry(process?.country ?? getDefaultCountry());
    setUnitTypes({
      inputUnit: process?.inputUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
      outputUnit: process?.outputUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
      sideStreamUnit: process?.sideStreamUnit ?? MEASUREMENT_TYPES.WEIGHT_KG
    });
    return normalizeProcessForForm({
      ...process,
      inputUnit: process?.inputUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
      outputUnit: process?.outputUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
      sideStreamUnit: process?.sideStreamUnit ?? MEASUREMENT_TYPES.WEIGHT_KG
    });
  }, [process, getDefaultCountry]);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors }
  } = useForm<ProcessFormValues>({
    mode: "all",
    defaultValues: values,
    values: values
  });

  const { mutate: updateProcess, isLoading } = useUpdateRecipeProcess({
    onSuccess: () => {
      modalClose();
    }
  });

  const { mutate: deleteProcess, isLoading: isProcessDeleting } = useDeleteRecipeProcess();

  const onSubmit = (data: ProcessFormValues) => {
    // Validate process name uniqueness
    if (recipeData) {
      const existingProcesses = getExistingProcessesInContext(recipeData, isOriginal, variantId);

      // Pass process._id to exclude current process from duplicate check
      if (!isProcessNameUnique(data.processName, existingProcesses, process._id)) {
        errorView("Process already exists");
        return;
      }
    }

    // Preserve relationship tracking fields during update
    // These fields maintain the audit trail from baseline to variant processes
    // and must remain intact through all edit operations including renames
    const payload = buildRecipeProcessPayload(data, {
      preserveRelationshipFrom: process,
      overrides: {
        country: Number(country),
        inputUnit: unitTypes.inputUnit,
        outputUnit: unitTypes.outputUnit,
        sideStreamUnit: unitTypes.sideStreamUnit,
        dataType: dataType,
        genericProcessId: toNullableApiNumber(processType)
      }
    });

    updateProcess({
      recipeId: recipeId ?? "",
      processId: process?._id ?? "",
      variantId: variantId ?? "",
      recipeType: isOriginal ? RecipeType.ORIGINAL : RecipeType.VARIANT,
      process: payload
    });
  };

  const handleDeleteProcess = () => {
    deleteProcess(
      {
        recipeId: recipeId ?? "",
        processId: process?._id ?? "",
        variantId: "",
        recipeType: isOriginal ? RecipeType.ORIGINAL : RecipeType.VARIANT
      },
      {
        onSuccess: () => modalClose()
      }
    );
  };

  if (isLoading || isCountriesDataLoading || isProcessDeleting) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <CommonModal
      modalOpenState={visibleModal}
      modalCloseState={() => {
        modalClose();
      }}
      centered={true}
      title="Update Processing Step"
    >
      <>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid>
            <Grid.Column computer={16} tablet={16} mobile={16} className="userBankDetailsMain">
              <CommonSplitContainer
                currentHeight={0.91}
                topChild={
                  <>
                    <Grid>
                      <Grid.Column computer={16} tablet={16} mobile={16}>
                        <InputText
                          register={register}
                          errors={errors?.processName}
                          labelName={"Processing Name*"}
                          placeholder="Processing Name"
                          name={`processName`}
                          required={true}
                          errorMessage={"Please enter processing Name"}
                        />
                      </Grid.Column>
                      <Grid.Column computer={8} tablet={8} mobile={16}>
                        <DropDown
                          labelName={"Data Type*"}
                          currentData={dataTypeOptions}
                          placeholder="Select Data Type"
                          defaultValue={dataType}
                          handleChangeState={(e: any, { value }: any) => {
                            setValue("dataType", value, { shouldDirty: true });
                            setDataType(value);
                          }}
                        />
                      </Grid.Column>
                      {dataTypeIsGeneric ? (
                        <Grid.Column computer={8} tablet={8} mobile={16}>
                          <DropDown
                            labelName={"Generic Process type"}
                            defaultValue={processType}
                            currentData={genericProcess}
                            errorMessage={"Origin country required"}
                            handleChangeState={(e: any, { value }: any) => {
                              setProcessType(value);
                            }}
                            placeholder="Select Process Type"
                            search
                            key={"process type"}
                          />
                        </Grid.Column>
                      ) : null}
                      {!dataTypeIsGeneric ? (
                        <>
                          <Grid.Column computer={8} tablet={8} mobile={16}>
                            <DropDown
                              labelName={"Processing Country*"}
                              defaultValue={country}
                              currentData={countries}
                              required
                              errorMessage={"Origin country required"}
                              handleChangeState={(e: any, { value }: any) => {
                                setCountry(value);
                              }}
                              placeholder="Select Processing Country"
                              search
                              key={"country"}
                            />
                          </Grid.Column>
                          <Grid.Column computer={4} tablet={8} mobile={16}>
                            <InputWithDropDown
                              defaultSelectValue={unitTypes.inputUnit}
                              options={WEIGHT_UNIT_TYPES_OPTIONs}
                              selectOption={(value: string | number) =>
                                setUnitTypes({
                                  ...unitTypes,
                                  inputUnit: value as MEASUREMENT_TYPES
                                })
                              }
                              control={control}
                              labelName={"Input*"}
                              placeholder="Input"
                              name={`input`}
                              errors={errors?.input}
                              errorMessage="Input is required"
                              customRequired={true}
                              valueAsNumber={true}
                              rightContentEnable={true}
                              rightContent={"Kg"}
                              transform={{
                                input: (value: any) =>
                                  isNaN(value) || value === 0 ? "" : value.toString(),
                                output: (e: any) => {
                                  return e;
                                }
                              }}
                              otherValidation={{
                                validate: {
                                  valueCheck: (v: any) =>
                                    valueGraterThanZero(v) || "Input must be grater than 0"
                                }
                              }}
                              required={true}
                            />
                          </Grid.Column>
                          <Grid.Column computer={4} tablet={8} mobile={16}>
                            <InputWithDropDown
                              defaultSelectValue={unitTypes.outputUnit}
                              options={WEIGHT_UNIT_TYPES_OPTIONs}
                              selectOption={(value: string | number) =>
                                setUnitTypes({
                                  ...unitTypes,
                                  outputUnit: value as MEASUREMENT_TYPES
                                })
                              }
                              control={control}
                              labelName={"Output*"}
                              placeholder="Output"
                              name={`output`}
                              errors={errors?.output}
                              errorMessage="Output is required"
                              customRequired={true}
                              valueAsNumber={true}
                              rightContentEnable={true}
                              rightContent={"Kg"}
                              transform={{
                                input: (value: any) =>
                                  isNaN(value) || value === 0 ? "" : value.toString(),
                                output: (e: any) => {
                                  return e;
                                }
                              }}
                              otherValidation={{
                                validate: {
                                  valueCheck: (v: any) =>
                                    valueGraterThanZero(v) || "Output must be grater than 0"
                                }
                              }}
                              required={true}
                            />
                          </Grid.Column>
                          <Grid.Column computer={8} tablet={8} mobile={16}>
                            <InputWithDropDown
                              defaultSelectValue={unitTypes.sideStreamUnit}
                              options={WEIGHT_UNIT_TYPES_OPTIONs}
                              selectOption={(value: string | number) =>
                                setUnitTypes({
                                  ...unitTypes,
                                  sideStreamUnit: value as MEASUREMENT_TYPES
                                })
                              }
                              control={control}
                              labelName={"Bio waste/side stream"}
                              placeholder="Bio waste/side stream"
                              name={`sideStream`}
                              errors={errors?.sideStream}
                              errorMessage="Bio waste/side stream is required"
                              rightContentEnable={true}
                              rightContent={"Kg"}
                              transform={{
                                input: (value: any) =>
                                  isNaN(value) || value === 0 ? "" : value.toString(),
                                output: (e: any) => {
                                  return e;
                                }
                              }}
                            />
                          </Grid.Column>
                          <Grid.Column computer={8} tablet={8} mobile={16}>
                            <InputNumber
                              control={control}
                              transform={{
                                input: (value: any) =>
                                  isNaN(value) || value === 0 ? "" : value.toString(),
                                output: (e: any) => {
                                  return e;
                                }
                              }}
                              labelName={"Electricity (per input amount)*"}
                              placeholder="0"
                              name={`electricity`}
                              rightContentEnable={true}
                              rightContent={"kWh/kg input"}
                              required={true}
                              errors={errors?.electricity}
                              errorMessage="Electricity is required"
                              otherValidation={{
                                validate: {
                                  valueCheck: (v: any) =>
                                    valueGraterThanZero(v) || "Electricity must be grater than 0"
                                }
                              }}
                            />
                          </Grid.Column>
                          <Grid.Column computer={8} tablet={8} mobile={16}>
                            <InputNumber
                              transform={{
                                input: (value: any) =>
                                  isNaN(value) || value === 0 ? "" : value.toString(),
                                output: (e: any) => {
                                  return e;
                                }
                              }}
                              control={control}
                              labelName={"Heat (per input amount)"}
                              placeholder="0"
                              name={`heat`}
                              errors={errors?.heat}
                              rightContentEnable={true}
                              rightContent={"MJ/kg of input"}
                            />
                          </Grid.Column>
                          <Grid.Column computer={8} tablet={8} mobile={16}>
                            <InputNumber
                              transform={{
                                input: (value: any) =>
                                  isNaN(value) || value === 0 ? "" : value.toString(),
                                output: (e: any) => {
                                  return e;
                                }
                              }}
                              control={control}
                              labelName={"Water (per input amount)"}
                              placeholder="0"
                              name={`wastewater`}
                              valueAsNumber={true}
                              rightContentEnable={true}
                              rightContent={"m3 per kg input"}
                            />
                          </Grid.Column>
                        </>
                      ) : null}
                      <Grid.Column computer={16} tablet={8} mobile={16}>
                        <InputTextArea
                          register={register}
                          customText="textAreaInput"
                          labelName={"Notes"}
                          name="processNote"
                        />
                      </Grid.Column>
                    </Grid>
                    <ConfirmModal
                      viewModal={visibleDeleteModal}
                      closeModal={() => setVisibleDeleteModal(false)}
                      cancel={() => {
                        setVisibleDeleteModal(false);
                      }}
                      approve={() => {
                        handleDeleteProcess();
                      }}
                      title={`Delete ${process?.processName || ""} Process`}
                      subTitle={`Are you sure you want to remove the ${
                        process?.processName || ""
                      } process? Please consider you cannot recover these data after remove`}
                    />
                  </>
                }
                bottomChild={
                  <MainBottomMultipleButtonView
                    leftButtons={[
                      {
                        title: "Delete",
                        onClick: () => setVisibleDeleteModal(true),
                        buttonOutLine: true
                      }
                    ]}
                    rightButtons={[
                      {
                        contentView: 16,
                        title: "Update",
                        onClick() {},
                        type: "submit",
                        buttonView: true
                      }
                    ]}
                  />
                }
              />
            </Grid.Column>
          </Grid>
        </form>
      </>
    </CommonModal>
  );
};

export default RecipeEditProcessingStepModal;
